import React, { useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";

import {
  CalendarMonthOutlined as CalendarIcon,
  Clear as ClearIcon,
  SaveOutlined as SaveIcon,
  TranslateOutlined as TranslateIcon,
} from "@mui/icons-material";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import dayjs from "dayjs";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import Editor from "../../Common/Components/Editor/Editor";

import "./css/SpecialMessageEditor.css";

function SpecialMessageEditor() {
  // ============================================================
  // STATE
  // ============================================================

  const userState = useSelector((state) => state.user);

  const { t } = useTranslation(["Articles"]);

  const [textFr, setTextFr] = useState("");
  const [textEn, setTextEn] = useState("");

  const [publicationDate, setPublicationDate] = useState(null);

  const [endPublicationDate, setEndPublicationDate] = useState(null);

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [alertOpen, setAlertOpen] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");

  const [alertType, setAlertType] = useState("success");

  // ============================================================
  // FETCH SPECIAL MESSAGE
  // ============================================================

  useEffect(() => {
    const fetchMessage = async () => {
      try {
        setLoading(true);

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/specialMessage/GetMessage`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userState.token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Unable to load message (${response.status})`);
        }

        const data = await response.json();

        setTextEn(data.textEn || "");
        setTextFr(data.textFr || "");

        setPublicationDate(
          data.publicationDate ? dayjs(data.publicationDate) : null,
        );

        setEndPublicationDate(
          data.endPublicationDate ? dayjs(data.endPublicationDate) : null,
        );
      } catch (error) {
        console.error("Error loading special message:", error);

        setAlertMessage("Unable to load the special message.");

        setAlertType("error");
        setAlertOpen(true);
      } finally {
        setLoading(false);
      }
    };

    if (userState.token) {
      fetchMessage();
    }
  }, [userState.token]);

  // ============================================================
  // CLEAR DATES
  // ============================================================

  const handleClearDate = () => {
    setPublicationDate(null);
    setEndPublicationDate(null);
  };

  // ============================================================
  // SAVE MESSAGE
  // ============================================================

  const handleSaveMessage = async () => {
    // ----------------------------------------------------------
    // Validate dates
    // ----------------------------------------------------------

    if (!publicationDate || !endPublicationDate) {
      setAlertMessage("Publication dates are mandatory.");

      setAlertType("warning");
      setAlertOpen(true);

      return;
    }

    // ----------------------------------------------------------
    // Validate date order
    // ----------------------------------------------------------

    if (dayjs(endPublicationDate).isBefore(dayjs(publicationDate))) {
      setAlertMessage(
        "The end publication date must be after the publication date.",
      );

      setAlertType("warning");
      setAlertOpen(true);

      return;
    }

    try {
      setSaving(true);

      const messageData = {
        TextFR: textFr,
        TextEN: textEn,
        Author: userState.username,

        publishedDate: dayjs(publicationDate).toISOString(),

        endPublishedDate: dayjs(endPublicationDate).toISOString(),
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/specialMessage/SaveMessageCasserole`,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",

            Authorization: `Bearer ${userState.token}`,
          },

          body: JSON.stringify(messageData),
        },
      );

      if (!response.ok) {
        throw new Error(`Save failed (${response.status})`);
      }

      setAlertMessage("Special message saved successfully.");

      setAlertType("success");
      setAlertOpen(true);
    } catch (error) {
      console.error("Error saving special message:", error);

      setAlertMessage("Unable to save the special message.");

      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // KEYBOARD EVENT
  // ============================================================

  const handleSaveWhenKeyPressed = async (event) => {
    // Kept intentionally for the Editor component.
    // You can add keyboard save behavior here later.
  };

  // ============================================================
  // LANGUAGE HEADER
  // ============================================================

  const LanguageHeader = ({ language, title }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1.5,

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          bgcolor: "action.hover",
        }}
      >
        <TranslateIcon color="primary" fontSize="small" />
      </Box>

      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          {language}
        </Typography>
      </Box>
    </Box>
  );

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "70vh",

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          bgcolor: "#f7f8fa",
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={40} />

          <Typography variant="body2" color="text.secondary">
            Loading special message...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // ============================================================
  // MAIN RENDER
  // ============================================================

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",

          bgcolor: "#f7f8fa",

          p: {
            xs: 2,
            md: 3,
          },
        }}
      >
        {/* ====================================================
            HEADER
        ==================================================== */}

        <Box
          sx={{
            mb: 3,

            display: "flex",

            justifyContent: "space-between",

            alignItems: {
              xs: "flex-start",
              md: "center",
            },

            flexDirection: {
              xs: "column",
              md: "row",
            },

            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight={700} color="text.primary">
              Special Message
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
              }}
            >
              Manage the message displayed to users and its publication period.
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            disabled={saving}
            onClick={handleSaveMessage}
            sx={{
              minWidth: 160,
              height: 44,

              borderRadius: 1.5,

              textTransform: "none",

              fontWeight: 600,
            }}
          >
            {saving ? "Saving..." : t("Save")}
          </Button>
        </Box>

        {/* ====================================================
            PUBLICATION PERIOD
        ==================================================== */}

        <Card
          sx={{
            mb: 3,

            borderRadius: 2,

            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent
            sx={{
              p: "18px !important",
            }}
          >
            <Box
              sx={{
                display: "flex",

                justifyContent: "space-between",

                alignItems: {
                  xs: "flex-start",
                  md: "center",
                },

                flexDirection: {
                  xs: "column",
                  md: "row",
                },

                gap: 2,

                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,

                    borderRadius: 1.5,

                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",

                    bgcolor: "action.hover",
                  }}
                >
                  <CalendarIcon color="primary" fontSize="small" />
                </Box>

                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Publication Period
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    Define when the message will be displayed.
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="outlined"
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleClearDate}
                disabled={!publicationDate && !endPublicationDate}
                sx={{
                  textTransform: "none",

                  borderRadius: 1.5,
                }}
              >
                {t("ClearDate")}
              </Button>
            </Box>

            <Divider sx={{ mb: 2.5 }} />

            <Grid container spacing={2}>
              {/* PUBLICATION DATE */}

              <Grid item xs={12} md={6}>
                <DatePicker
                  label={t("PublicationDate")}
                  value={publicationDate}
                  onChange={(newValue) => setPublicationDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                    },
                  }}
                />
              </Grid>

              {/* END PUBLICATION DATE */}

              <Grid item xs={12} md={6}>
                <DatePicker
                  label={t("EndPublishedDate")}
                  value={endPublicationDate}
                  minDate={publicationDate || undefined}
                  onChange={(newValue) => setEndPublicationDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ====================================================
            MESSAGE CONTENT
        ==================================================== */}

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            Message Content
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Create the message in both supported languages.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {/* ==================================================
              FRENCH EDITOR
          ================================================== */}

          <Grid item xs={12} lg={6}>
            <Card
              sx={{
                height: "100%",

                borderRadius: 2,

                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",

                overflow: "hidden",
              }}
            >
              <CardContent
                sx={{
                  p: "18px !important",
                }}
              >
                <LanguageHeader language="FR" title="French" />

                <Divider
                  sx={{
                    my: 2,
                  }}
                />

                <Box
                  sx={{
                    minHeight: 350,
                  }}
                >
                  <Editor
                    title={null}
                    setTitle={null}
                    setText={setTextFr}
                    text={textFr}
                    readOnly={false}
                    onKeyPressed={handleSaveWhenKeyPressed}
                    toolbarId={"toolbarSpecialMessageFr"}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* ==================================================
              ENGLISH EDITOR
          ================================================== */}

          <Grid item xs={12} lg={6}>
            <Card
              sx={{
                height: "100%",

                borderRadius: 2,

                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",

                overflow: "hidden",
              }}
            >
              <CardContent
                sx={{
                  p: "18px !important",
                }}
              >
                <LanguageHeader language="EN" title="English" />

                <Divider
                  sx={{
                    my: 2,
                  }}
                />

                <Box
                  sx={{
                    minHeight: 350,
                  }}
                >
                  <Editor
                    title={null}
                    setTitle={null}
                    setText={setTextEn}
                    text={textEn}
                    readOnly={false}
                    onKeyPressed={handleSaveWhenKeyPressed}
                    toolbarId={"toolbarSpecialMessageEn"}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ====================================================
            BOTTOM ACTIONS
        ==================================================== */}

        <Box
          sx={{
            mt: 3,

            display: "flex",

            justifyContent: "flex-end",

            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearDate}
            disabled={saving || (!publicationDate && !endPublicationDate)}
            sx={{
              height: 42,

              textTransform: "none",

              borderRadius: 1.5,
            }}
          >
            {t("ClearDate")}
          </Button>

          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            disabled={saving}
            onClick={handleSaveMessage}
            sx={{
              height: 42,
              minWidth: 150,

              textTransform: "none",

              borderRadius: 1.5,

              fontWeight: 600,
            }}
          >
            {saving ? "Saving..." : t("Save")}
          </Button>
        </Box>

        {/* ====================================================
            ALERT
        ==================================================== */}

        <Snackbar
          open={alertOpen}
          autoHideDuration={5000}
          onClose={() => setAlertOpen(false)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
        >
          <Alert
            onClose={() => setAlertOpen(false)}
            severity={alertType}
            variant="filled"
            sx={{
              width: "100%",
            }}
          >
            {alertMessage}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}

export default SpecialMessageEditor;
